/**
 * _helpers.ts — extracted from block-renderer.tsx (SPEC-244 IA16-LANDING-FE).
 * Cero cambios de lógica — VERBATIM extraction. La composición la hace
 * el dispatcher block-renderer.tsx (importa los renderers por tipo).
 * Spec ref: SPEC-059 (docs/SPEC-059-LANDING-PAGE-BUILDER.md).
 */

import * as React from 'react';

/**
 * Safely parse a YouTube/Vimeo URL into an embeddable iframe URL.
 * Supports:
 *   - youtube.com/watch?v=ID
 *   - youtu.be/ID
 *   - youtube.com/embed/ID
 *   - vimeo.com/ID
 *   - player.vimeo.com/video/ID
 *
 * Returns the original URL if it doesn't match any known pattern (the
 * caller decides whether to render it — defense-in-depth, the renderer
 * below only renders `embedUrl` if it's an http(s) URL).
 */
export function toEmbedUrl(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;

  // YouTube watch?v=ID
  const ytWatch = trimmed.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{6,})/);
  if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;

  // youtu.be/ID
  const ytShort = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;

  // youtube.com/embed/ID (already embedded)
  if (/youtube\.com\/embed\//.test(trimmed)) return trimmed;

  // player.vimeo.com/video/ID (already embedded)
  if (/player\.vimeo\.com\/video\//.test(trimmed)) return trimmed;

  // vimeo.com/ID
  const vimeo = trimmed.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  // Unknown — return as-is (the caller checks isHttpUrl before rendering).
  return trimmed;
}

export function isHttpUrl(raw: string | undefined | null): raw is string {
  if (!raw || typeof raw !== 'string') return false;
  return /^https?:\/\//i.test(raw.trim());
}

/**
 * [SPRINT 58 · PATCH 1 — hero fallback] Luminance check for theme colors.
 *
 * The hero fallback gradient (see HeroBlock) uses the theme's primary /
 * secondary colors as its endpoints. `primaryColor` is the theme's TEXT
 * color — normally dark (light page theme), but LIGHT on dark themes
 * (e.g. the applied system landing's per-block textColor #F8FAFC). Using
 * a light color as the gradient's dark endpoint would re-create the
 * invisible-white-text bug we're fixing. This guard accepts only hex
 * colors dark enough (< 0.35 relative luminance) as gradient endpoints;
 * anything else falls back to the fixed slate #0f172a.
 */
export function isDarkHex(hex: string | undefined | null): boolean {
  if (!hex || typeof hex !== 'string') return false;
  const trimmed = hex.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) return false;
  const h = trimmed.slice(1);
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.35;
}

/**
 * [FIX-LANDING-STYLING-WHITE-ON-WHITE] Pick a readable Tailwind text-color
 * class for content painted on top of the given background color.
 *
 * Returns `'text-white'` when the background is dark enough for white text
 * to be readable (WCAG AA), and `'text-slate-900'` when the background is
 * light. When `bgColor` is missing or unparseable, falls back to
 * `'text-white'` (preserves the legacy "white text" behavior for the hero
 * gradient fallback, which is always dark enough at the dark endpoint).
 *
 * Used by `HeroBlock` + `CtaBlock` to choose between `text-white` and
 * `text-slate-900` so the white-on-white bug ( Founder report:
 * "white text appears on white backgrounds in landing page templates +
 * the default landing page for all users") cannot occur:
 *   - Default state (no per-block theme, no bg image): the hero gradient
 *     fallback fires → `bgStyle.backgroundImage` is set → callers pass
 *     the gradient as the "effective bg" (which `isDarkHex` rejects
 *     because it's not a hex string) → falls back to `'text-white'`.
 *     Correct (gradient is always dark enough).
 *   - User sets `block.theme.backgroundColor` to a LIGHT color (the bug
 *     scenario): `isDarkHex(bgColor)` returns false → returns
 *     `'text-slate-900'` → dark text on light bg. Readable.
 *   - User sets `block.theme.backgroundColor` to a DARK color:
 *     `isDarkHex(bgColor)` returns true → returns `'text-white'` →
 *     white text on dark bg. Readable.
 *
 * Anti-P-041: no `as any`. Anti-P-042: no `console.*`. Anti-P-383: file
 * stays well under 500L (this helper is ~20 lines added).
 */
export function readableTextOn(
  bgColor: string | undefined | null,
): 'text-white' | 'text-slate-900' {
  // Only flip to dark text when we have a valid LIGHT hex bg. When bg is
  // dark, missing, or unparseable (e.g. the gradient string from the
  // fallback path), keep the legacy `text-white`.
  if (!bgColor) return 'text-white';
  if (!isDarkHex(bgColor)) return 'text-slate-900';
  return 'text-white';
}
